import React from "react";

// How far one arrow key press moves a split, in px.
export const RESIZE_STEP = 24;

// `value` kept inside [min, max]. When the room runs out (max < min), min wins,
// which is what every layout's drag handler did before this was shared.
export const clampSize = (value, min, max) => Math.max(min, Math.min(Math.max(min, max), value));

/**
 * The draggable border between two workspace panes (`.pl-resizer`, styled in
 * PromptLabLayout.css). Dragging stays with the layout's own pointer handler;
 * this adds the keyboard. Arrow keys move the split by RESIZE_STEP px, Home and
 * End jump to its limits.
 *
 * `value` is the size of the pane the divider controls, `min` and `max` its
 * limits right now. `orientation` is the divider's own: a "vertical" one sits
 * between columns and moves with Left/Right, a "horizontal" one between rows
 * and moves with Up/Down.
 */
const PaneResizer = ({ orientation = "vertical", value, min, max, onResize, onPointerDown, label, title }) => {
    const top = Math.max(min, max);

    const onKeyDown = (e) => {
        const less = orientation === "vertical" ? "ArrowLeft" : "ArrowUp";
        const more = orientation === "vertical" ? "ArrowRight" : "ArrowDown";
        let next;
        if (e.key === less) next = value - RESIZE_STEP;
        else if (e.key === more) next = value + RESIZE_STEP;
        else if (e.key === "Home") next = min;
        else if (e.key === "End") next = top;
        else return;
        e.preventDefault();
        onResize(clampSize(next, min, top));
    };

    return (
        <div
            className={`pl-resizer pl-resizer--${orientation === "vertical" ? "col" : "row"}`}
            onPointerDown={onPointerDown}
            onKeyDown={onKeyDown}
            role="separator"
            tabIndex={0}
            aria-orientation={orientation}
            aria-label={label}
            aria-valuemin={Math.round(min)}
            aria-valuemax={Math.round(top)}
            aria-valuenow={Math.round(clampSize(value, min, top))}
            title={title}
        />
    );
};

export default PaneResizer;
